import type { MessagePart, ToolExecution } from '@/contexts/ai-assistant-context'

/** Tool name for the questionnaire flow — its `AgentQuestionnaireCard` already
 * shows progress, so the generic tool marker must not duplicate it. */
export const QUESTIONNAIRE_TOOL_NAME = 'request_user_input'

/** Concatenates every text part's content, in order — the flat text used to drive the reveal animation and `assistant-status` heuristics. */
export function getMessageText(parts: MessagePart[]): string {
  return parts
    .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('')
}

/** Every tool part's `ToolExecution`, in order. */
export function getMessageTools(parts: MessagePart[]): ToolExecution[] {
  return parts
    .filter((part): part is Extract<MessagePart, { type: 'tool' }> => part.type === 'tool')
    .map((part) => part.tool)
}

export function formatToolName(toolName: string): string {
  const names: Record<string, string> = {
    search_contacts: 'Searching Contacts',
    search_inquiries: 'Searching Leads',
    search_events: 'Searching Events',
    search_bookings: 'Searching Bookings',
    get_organization: 'Getting Organization Info',
    get_user: 'Getting User Info',
    update_inquiry_stage: 'Updating Lead Stage',
    update_event_stage: 'Updating Event Stage',
    render_data: 'Pulling up records',
  get_operating_brief: 'Preparing Operating Brief',
  get_case_brief: 'Preparing Case Brief',
  list_tasks: 'Finding Tasks',
  get_task: 'Getting Task Details',
  search_team_members: 'Finding Team Members',
  create_task: 'Preparing Task',
  update_task: 'Preparing Task Update',
    show_results: 'Showing Results',
  }
  // Fallback: replace underscores and capitalize first letter of each word.
  // Also covers tool names not yet in the map above (e.g. "get_inquiry",
  // "create_inquiry") so they don't leak the internal "Inquiry" wording.
  return names[toolName] || humanizeActionType(toolName)
}

// Cortado's external product label for the "inquiry" entity is "Lead" —
// wire/dispatch identifiers (tool names, action_type values) intentionally
// stay "inquiry"/"inquiries", but any humanized display text should read as
// "Lead"/"Leads" instead. Case of the replacement follows the case of the
// match, so this is safe to run on both title-cased and lowercase text.
export function toLeadWording(text: string): string {
  return text
    .replace(/\binquiries\b/gi, (match) => (match[0] === match[0].toUpperCase() ? 'Leads' : 'leads'))
    .replace(/\binquiry\b/gi, (match) => (match[0] === match[0].toUpperCase() ? 'Lead' : 'lead'))
}

// Humanize a raw action_type string (e.g. "update_inquiry_stage") into a
// title-cased label for use when a proposal has no server-provided preview title.
export function humanizeActionType(actionType: string): string {
  return toLeadWording(actionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
}
